import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { marketPulseService } from '../../services/marketPulseService';

/** A single leg of an options trade. */
export interface TradeLeg {
  side: 'buy' | 'sell';
  option_type: 'C' | 'P';
  strike: number;
  expiry?: string;
  ticker?: string;
}

/** Props for the reusable trade entry dialog. */
export interface TradeEntryDialogProps {
  open: boolean;
  ticker: string;
  strategyName: string;
  legs: TradeLeg[];
  expiry?: string;
  onClose: () => void;
  onSubmit: (data: TradeEntryData) => Promise<void>;
}

/** Data returned when the user confirms the trade. */
export interface TradeEntryData {
  method: 'log' | 'execute';
  ticker: string;
  strategyName: string;
  contracts: number;
  entry_price: number;
  open_date: string;
  expiry: string;
  notes: string;
  legs: TradeLeg[];
}

const TradeEntryDialog: React.FC<TradeEntryDialogProps> = ({
  open, ticker, strategyName: initialStrategyName, legs: initialLegs, expiry, onClose, onSubmit,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isManual = initialLegs.length === 0;

  const [method, setMethod] = useState<'execute' | 'log'>('log');
  const [strategyName, setStrategyName] = useState(initialStrategyName === 'manual' ? '' : initialStrategyName);
  const [editableLegs, setEditableLegs] = useState<TradeLeg[]>(
    isManual
      ? [{ side: 'buy', option_type: 'C', strike: 0, expiry: expiry || '' }]
      : initialLegs.map((l) => ({ ...l, expiry: l.expiry || expiry || '' }))
  );
  const [contracts, setContracts] = useState('1');
  const [entryPrice, setEntryPrice] = useState('');
  const [openDate, setOpenDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingPrice, setFetchingPrice] = useState(false);
  const [error, setError] = useState('');
  const [availableStrikes, setAvailableStrikes] = useState<Record<string, number[]>>({});

  // Fetch available strikes when expiry or option_type changes on any leg
  const strikeFetchKeys = useMemo(() => {
    const keys = new Set<string>();
    editableLegs.forEach((l) => {
      if (l.expiry && l.option_type) keys.add(`${l.expiry}|${l.option_type}`);
    });
    return Array.from(keys);
  }, [editableLegs]);

  useEffect(() => {
    strikeFetchKeys.forEach((key) => {
      if (availableStrikes[key]) return; // already fetched
      const [exp, ot] = key.split('|');
      marketPulseService.getOptionStrikes(ticker, exp, ot)
        .then((strikes) => setAvailableStrikes((prev) => ({ ...prev, [key]: strikes })))
        .catch(() => {});
    });
  }, [strikeFetchKeys, ticker]); // eslint-disable-line react-hooks/exhaustive-deps

  const addLeg = () => setEditableLegs([...editableLegs, { side: 'sell', option_type: 'C', strike: 0, expiry: editableLegs[0]?.expiry || '' }]);
  const removeLeg = (i: number) => setEditableLegs(editableLegs.filter((_, idx) => idx !== i));
  const updateLeg = (i: number, field: keyof TradeLeg, value: string | number) => {
    const updated = [...editableLegs];
    updated[i] = { ...updated[i], [field]: value };
    setEditableLegs(updated);
  };

  // Auto-fetch option price when all legs have strike + expiry
  const fetchPrice = useCallback(async () => {
    const validLegs = editableLegs.filter((l) => l.strike > 0 && l.expiry);
    if (validLegs.length === 0 || validLegs.length !== editableLegs.length) return;

    setFetchingPrice(true);
    try {
      let totalMid = 0;
      for (const leg of validLegs) {
        const result = await marketPulseService.getOptionPrice(ticker, leg.strike, leg.expiry!, leg.option_type);
        if (result.mid != null) {
          totalMid += leg.side === 'buy' ? result.mid : -result.mid;
        }
      }
      // For credit trades, show as positive (premium received)
      // For debit trades, show as positive (cost paid)
      setEntryPrice(Math.abs(totalMid).toFixed(2));
    } catch {
      // Silently fail — user can enter manually
    } finally {
      setFetchingPrice(false);
    }
  }, [editableLegs, ticker]);

  // Debounce the price fetch
  useEffect(() => {
    const allFilled = editableLegs.every((l) => l.strike > 0 && l.expiry);
    if (!allFilled) return;
    const timer = setTimeout(fetchPrice, 500);
    return () => clearTimeout(timer);
  }, [editableLegs, fetchPrice]);

  const handleConfirm = async () => {
    if (method === 'execute') return;
    const c = parseInt(contracts, 10);
    const p = parseFloat(entryPrice);
    if (!strategyName.trim() && isManual) {
      // Auto-infer for manual mode
    }
    if (isNaN(c) || c <= 0) { setError('Enter a valid number of contracts'); return; }
    if (isNaN(p) || p < 0) { setError('Enter a valid entry price'); return; }
    const validLegs = editableLegs.filter((l) => l.strike > 0).map((l) => ({ ...l, ticker }));
    if (validLegs.length === 0) { setError('Add at least one leg with a strike price'); return; }
    if (validLegs.some((l) => !l.expiry)) { setError('Enter an expiration date for each leg'); return; }

    // Auto-infer strategy name from legs if not provided
    let finalName = strategyName.trim();
    if (!finalName || finalName === 'manual') {
      const buys = validLegs.filter((l) => l.side === 'buy');
      const sells = validLegs.filter((l) => l.side === 'sell');
      if (validLegs.length === 1) {
        const l = validLegs[0];
        const type = l.option_type === 'C' ? 'call' : 'put';
        finalName = l.side === 'buy' ? `long_${type}` : `short_${type}`;
      } else if (buys.length === 1 && sells.length === 1) {
        const buyType = buys[0].option_type;
        const sellType = sells[0].option_type;
        if (buyType === sellType) {
          const type = buyType === 'C' ? 'call' : 'put';
          // Calls: buy lower strike = debit. Puts: buy higher strike = debit.
          const isDebit = buyType === 'C'
            ? buys[0].strike < sells[0].strike
            : buys[0].strike > sells[0].strike;
          finalName = isDebit ? `${type}_debit_spread` : `${type}_credit_spread`;
        } else {
          finalName = 'combo';
        }
      } else if (validLegs.length === 4) {
        finalName = 'iron_condor';
      } else {
        finalName = `${validLegs.length}_leg_spread`;
      }
    }

    setError('');
    setLoading(true);
    try {
      await onSubmit({ method: 'log', ticker, strategyName: finalName, contracts: c, entry_price: p, open_date: openDate, expiry: validLegs[0].expiry || '', notes, legs: validLegs });
      onClose();
    } catch (e: any) {
      setError(e.message || 'Failed to log trade');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} maxWidth="sm" fullWidth>
      <DialogTitle>{isManual ? `Log Trade — ${ticker}` : `${initialStrategyName} — ${ticker}`}</DialogTitle>
      <DialogContent>
        <RadioGroup value={method} onChange={(e) => setMethod(e.target.value as 'execute' | 'log')} row={!fullScreen}>
          <FormControlLabel
            value="execute"
            control={<Radio size="small" />}
            label={<Box><Typography variant="body2" fontWeight={600}>Execute Trade</Typography><Typography variant="caption" color="text.secondary">via Broker API</Typography></Box>}
          />
          <FormControlLabel
            value="log"
            control={<Radio size="small" />}
            label={<Box><Typography variant="body2" fontWeight={600}>Log Trade</Typography><Typography variant="caption" color="text.secondary">manual entry</Typography></Box>}
          />
        </RadioGroup>

        <Divider sx={{ my: 2 }} />

        {method === 'execute' ? (
          <Alert severity="info">Broker API execution coming soon. Use <strong>Log Trade</strong> to record this position.</Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Strategy name */}
            <TextField
              label="Strategy"
              value={strategyName}
              onChange={(e) => setStrategyName(e.target.value)}
              size="small"
              placeholder="e.g. long_call, put_credit_spread, iron_condor"
              disabled={!isManual}
            />

            {/* Legs — always editable */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Legs</Typography>
              {editableLegs.map((leg, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <FormControl size="small" sx={{ minWidth: 80 }}>
                    <InputLabel>Side</InputLabel>
                    <Select value={leg.side} label="Side" onChange={(e) => updateLeg(i, 'side', e.target.value)}>
                      <MenuItem value="buy">Buy (+)</MenuItem>
                      <MenuItem value="sell">Sell (-)</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 80 }}>
                    <InputLabel>Type</InputLabel>
                    <Select value={leg.option_type} label="Type" onChange={(e) => updateLeg(i, 'option_type', e.target.value)}>
                      <MenuItem value="C">Call</MenuItem>
                      <MenuItem value="P">Put</MenuItem>
                    </Select>
                  </FormControl>
                  <Autocomplete
                    freeSolo
                    options={(() => {
                      const key = `${leg.expiry}|${leg.option_type}`;
                      return (availableStrikes[key] || []).map((s) => s.toString());
                    })()}
                    value={leg.strike ? leg.strike.toString() : ''}
                    onChange={(_, val) => updateLeg(i, 'strike', parseFloat(val || '0') || 0)}
                    onInputChange={(_, val) => updateLeg(i, 'strike', parseFloat(val || '0') || 0)}
                    renderInput={(params) => (
                      <TextField {...params} label="Strike" size="small" sx={{ width: 120 }} />
                    )}
                    sx={{ width: 120 }}
                    size="small"
                  />
                  <TextField
                    label="Expiry"
                    type="date"
                    value={leg.expiry || ''}
                    onChange={(e) => updateLeg(i, 'expiry', e.target.value)}
                    size="small"
                    sx={{ width: 150 }}
                    InputLabelProps={{ shrink: true }}
                  />
                  {editableLegs.length > 1 && (
                    <IconButton size="small" onClick={() => removeLeg(i)}><DeleteIcon fontSize="small" /></IconButton>
                  )}
                </Box>
              ))}
              <Button size="small" startIcon={<AddIcon />} onClick={addLeg}>Add Leg</Button>
            </Box>

            <TextField
              label="Contracts"
              type="number"
              value={contracts}
              onChange={(e) => setContracts(e.target.value)}
              inputProps={{ min: 1, step: 1 }}
              size="small"
              required
            />
            <TextField
              label={editableLegs.length > 1 ? 'Net debit/credit (per share)' : 'Premium (per share)'}
              type="number"
              value={entryPrice}
              onChange={(e) => setEntryPrice(e.target.value)}
              inputProps={{ min: 0, step: 0.01 }}
              size="small"
              required
              helperText={fetchingPrice ? 'Fetching latest price...' : (editableLegs.length > 1 ? 'Positive = debit paid, Negative = credit received' : undefined)}
              InputProps={{
                endAdornment: fetchingPrice ? <CircularProgress size={16} /> : undefined,
              }}
            />
            <TextField
              label="Open date"
              type="date"
              value={openDate}
              onChange={(e) => setOpenDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Why are you entering this trade?"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={2}
              size="small"
              placeholder="e.g. Bullish breakout above resistance, expecting continuation to $380"
            />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={loading || method === 'execute'}>
          {loading ? 'Saving...' : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TradeEntryDialog;
